import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Icon from '../Icon';
import formatDateAndTime from '../../utils/dateUtils';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../constants/dateFormats';

import ReservationButtonList from '../ReservationButtonList';
import Rga from '../Rga';
import TranslationProvider from '../../providers/TranslationProvider';
import Button from '../Button';
import eventTypes from '../../constants/eventTypes';
import Lock from '../../svg/icons/ic_lock_20x20.svg';

import './EventTicketType.scss';
import { selectSearchSessionId } from '../../reducers/venueSearchSlice';

const { GENERAL_ADMISSION, SEATED } = eventTypes;

const info = {
  comingSoon: {
    key: 'event_on_sale_soon',
    copy: 'On Sale Soon',
  },
  presale: {
    key: 'event_on_presale_soon',
    copy: 'Book Pre-Sale',
  },
  book: {
    key: 'book_now',
    copy: 'Book Now',
  },
  soldOut: {
    key: 'event_sold_out',
    copy: 'Sold Out',
  },
  presaleSoldOut: {
    key: 'event_presale_sold_out',
    copy: 'Pre-Sale Sold Out',
  },
  nLeft: (n) => {
    if (n === 1) {
      return {
        key: 'event_one_ticket_left',
        copy: 'Sorry, we only have 1 ticket left.',
      };
    }
    if (n === 0) {
      return {
        key: 'event_no_tickets_available',
        copy: 'No tickets available.',
      };
    }
    return {
      key: 'event_some_tickets_left',
      copy: `Sorry, we only have ${n} tickets left.`,
    };
  },
  noAvailability: {
    key: 'event_quantity_not_available',
    copy: 'Sorry, we don&apos;t have that quantity.',
  },
};

const renderGeneralSaleDate = (generalSaleDate, timezone) => {
  const { formattedDay: fDay, formattedTime: fTime } = formatDateAndTime(generalSaleDate, timezone);

  return (
    <TranslationProvider
      translationKey="event_on_sale"
      translationValues={{ date: fDay, time: fTime }}
    >
      <span className="EventTicketType__coming-soon-date">
        {`On Sale ${fDay} at ${fTime}.`}
      </span>
    </TranslationProvider>
  );
};

const renderComingSoonGeneralAdmission = (
  onClick,
  title,
  generalSaleDate,
  timezone
) => (
  <div className="EventTicketType__cta-container">
    <Button
      onClick={onClick}
      variant="primary"
      aria-label={`Coming Soon ticket for ${title}`}
      disabled
    >
      <TranslationProvider
        translationKey={info.comingSoon.key}
      >
        {info.comingSoon.copy}
      </TranslationProvider>
    </Button>
    {generalSaleDate
        && renderGeneralSaleDate(generalSaleDate, timezone)}
  </div>
);

const renderPresaleGeneralAdmission = (
  onClick,
  isGda,
  userIsGda,
  title
) => (
  <Button
    data-testid="presale-GA-button"
    onClick={onClick}
    variant={isGda ? 'rga' : 'primary'}
    aria-label={`Book Pre-Sale ticket for ${title}`}
    disabled={!userIsGda}
  >
    <TranslationProvider
      translationKey={info.presale.key}
    >
      {info.presale.copy}
    </TranslationProvider>
  </Button>
);

const renderBookNowGeneralAdmission = (
  onClick,
  isGda,
  userIsGda,
  title,
  enableBookNow
) => (
  <Button
    data-testid="book-now-GA-button"
    onClick={onClick}
    variant={(isGda && userIsGda) ? 'rga' : 'primary'}
    aria-label={`Book Now a ticket for ${title}`}
    disabled={!enableBookNow}
  >
    <TranslationProvider
      translationKey={info.book.key}
    >
      {info.book.copy}
    </TranslationProvider>
  </Button>
);

const renderBookNowGeneralSeated = (
  reservations,
  selectResy,
  eventName,
  title,
  type,
  quantity
) => {
  const isEvent = true;
  return (
    <ReservationButtonList
      ariaDescribedBy={`ticket-type-${title}`}
      reservations={reservations}
      onReservationSelect={selectResy}
      isEvent={isEvent}
      eventName={eventName}
      shouldExpand
      hideTableType={type !== SEATED}
      lineLimit={1}
      context="event-ticket"
      partySize={quantity}
    />
  );
};

const getBookNowOnClick = (
  reservation,
  selectResy
) => () => selectResy(reservation);

const renderComingSoon = (
  props
) => {
  const {
    reservations,
    onSelectResy,
    title,
    generalSaleDate,
    timezone,
  } = props;

  return renderComingSoonGeneralAdmission(
    getBookNowOnClick(reservations[0], onSelectResy),
    title,
    generalSaleDate,
    timezone
  );
};

const renderBookNow = (
  props
) => {
  const {
    generalAdmission,
    isGda,
    userIsGda,
    isPresale,
    reservations,
    onSelectResy,
    title,
    isPresaleSoldOut,
    eventName,
    enableBookNow,
    type,
    selectedQuantity,
  } = props;

  const isPresaleGdaUser = isPresale && isGda && userIsGda;

  if (generalAdmission) {
    if (isPresaleGdaUser && !isPresaleSoldOut) {
      return renderPresaleGeneralAdmission(
        getBookNowOnClick(reservations[0], onSelectResy),
        isGda,
        userIsGda,
        title
      );
    }

    return renderBookNowGeneralAdmission(
      getBookNowOnClick(reservations[0], onSelectResy),
      isGda,
      userIsGda,
      title,
      enableBookNow
    );
  }

  return renderBookNowGeneralSeated(
    reservations,
    onSelectResy,
    eventName,
    title,
    type,
    selectedQuantity
  );
};

const renderDescription = (arr) => (
  <div className="EventTicketType__info-description">
    <ul>{arr.map((txt) => <li key={txt?.substring(0, 10)}>{txt}</li>)}</ul>
  </div>
);

const getTaxForPrice = (taxIncluded) => {
  const taxNotIncludedCopy = (
    <TranslationProvider
      translationKey="tax"
    >
      <span> + tax</span>
    </TranslationProvider>
  );

  const taxIncludedCopy = (
    <TranslationProvider
      translationKey="taxes_included"
    >
      <span> (tax included)</span>
    </TranslationProvider>
  );
  return taxIncluded ? taxIncludedCopy : taxNotIncludedCopy;
};

const renderPrice = ({
  priceNumber, price, type, mandatoryAddOns, taxIncluded,
}) => {
  if (!priceNumber) {
    return null;
  }

  const labelString = type === 'seated' ? 'guest' : 'ticket';
  const label = (
    <TranslationProvider
      translationKey={`per_${labelString}`}
    >
      <span>{`per ${labelString}`}</span>
    </TranslationProvider>
  );
  const tax = getTaxForPrice(taxIncluded);

  const fromPrice = (
    <TranslationProvider
      translationKey="from_price"
      translationValues={{ price }}
    >
      <span>
        {`From ${price}`}
      </span>
    </TranslationProvider>
  );

  return (
    <div className="EventTicketType__info-price">
      {mandatoryAddOns ? fromPrice : price}
      {' '}
      {label}
      {tax}
    </div>
  );
};

const renderSaleHasEnded = () => (
  <TranslationProvider
    translationKey="sorry_sale_has_ended"
  >
    <div className="EventTicketType__info-description-limited grey">Sorry, sale has ended</div>
  </TranslationProvider>
);

const renderSoldOut = (props) => {
  const {
    soldOut,
    isGda,
    isPresaleSoldOut,
    generalSaleDate,
    publishDate,
  } = props;

  if (isPresaleSoldOut) {
    return (
      <div className="EventTicketType__cta-container">
        <Button disabled variant={isGda ? 'rga' : 'primary'}>
          <TranslationProvider
            translationKey={info.presaleSoldOut.key}
          >
            {info.presaleSoldOut.copy}
          </TranslationProvider>
        </Button>
        {
          generalSaleDate
          && !soldOut
          && renderGeneralSaleDate(generalSaleDate, publishDate)
        }
      </div>
    );
  }

  return (
    <Button disabled variant={isGda ? 'rga' : 'primary'}>
      <TranslationProvider
        translationKey={info.soldOut.key}
      >
        {info.soldOut.copy}
      </TranslationProvider>
    </Button>
  );
};

const renderQuantityMoreThanAvailable = (props) => {
  const {
    quantityMoreThanTickets, availableQuantity, generalAdmission,
  } = props;

  if (generalAdmission && quantityMoreThanTickets) {
    return (
      <TranslationProvider
        translationKey={info.nLeft(availableQuantity).key}
        translationValues={{ n: availableQuantity }}
      >
        <div className="EventTicketType__info-description-limited grey">
          {info.nLeft(availableQuantity).copy}
        </div>
      </TranslationProvider>
    );
  }

  return (
    <TranslationProvider
      translationKey={info.noAvailability.key}
    >
      <div className="EventTicketType__info-description-limited grey">
        {info.noAvailability.copy}
      </div>
    </TranslationProvider>
  );
};

const EventTicketType = (props) => {
  const {
    Analytics,
    availableQuantity,
    basePriceNumber,
    description,
    event,
    userIsGda,
    isGda,
    isPresale,
    isPreSaleOnForGdaUsers,
    isPresaleSoldOut,
    price,
    priceNumber,
    publishDate,
    selectedQuantity,
    selectResy,
    slots,
    soldOut,
    taxIncluded,
    title,
    type,
    enableBookNow,
    isEventNotOnSaleYet,
    generalSaleDate: saleDate,
    mandatoryAddOns,
  } = props;

  const {
    event: {
      saleHasEnded,
      generalSaleDate,
    },
  } = props;

  const searchSessionId = useSelector(selectSearchSessionId);

  const eventName = event.title;
  let quantityMoreThanTickets = selectedQuantity > availableQuantity;
  let bookNow = availableQuantity >= selectedQuantity;

  const presaleWithNoAvailability = (
    !bookNow
    && isPresale
    && !soldOut
    && availableQuantity === 0
  );

  const comingSoon = (userIsGda)
    ? presaleWithNoAvailability && !isPresaleSoldOut && !isPreSaleOnForGdaUsers
    : presaleWithNoAvailability;

  const generalAdmission = type === GENERAL_ADMISSION;
  const reservations = (slots || []).map((slot) => ({ ...slot, event }));

  if (type === SEATED) {
    quantityMoreThanTickets = availableQuantity === 0;
    bookNow = availableQuantity > 0;
  }

  const onSelectResy = (reservation, sId) => {
    const format = type === GENERAL_ADMISSION ? DATE_FORMAT : DATE_TIME_FORMAT;
    const time = reservation?.time ? moment(reservation.time).format(format) : undefined;
    Analytics.event('click booking slot', {
      venue: reservation?.event?.venue,
      city: reservation?.event?.location?.city,
      'is event': !!(reservation?.event),
      event: reservation?.event?.title,
      'party size': selectedQuantity,
      ...(time && { time }),
      'lead time': moment(reservation?.time).diff(moment().startOf('day'), 'days'),
      searchSessionId,
      details: {
        city: reservation?.event?.location?.city,
        serviceType: event?.type,
        venueId: event?.venueId,
      },
    });
    selectResy(
      { ...reservation, price: basePriceNumber },
      sId
    );
  };

  return (
    <div className="EventTicketType">
      <h2 className="EventTicketType__info-title" id={`ticket-type-${title}`}>
        {title}
        {isEventNotOnSaleYet
          && (
          <Icon
            data-testid="lock-icon"
            className="EventTicketType__info-title__icon"
            component={Lock}
            name="lock"
          />
          )}
      </h2>
      {renderPrice({
        priceNumber, price, type, mandatoryAddOns, taxIncluded,
      })}

      {isGda && <Rga />}

      {renderDescription(description)}

      {bookNow && !isEventNotOnSaleYet && renderBookNow({
        generalAdmission,
        isGda,
        userIsGda,
        isPresale,
        reservations,
        onSelectResy,
        title,
        soldOut,
        isPresaleSoldOut,
        eventName,
        enableBookNow,
        type,
      })}

      {(comingSoon || isEventNotOnSaleYet) && renderComingSoon({
        reservations,
        onSelectResy,
        title,
        generalSaleDate: saleDate,
        timezone: event?.timezone,
      })}

      {saleHasEnded && renderSaleHasEnded()}

      {!saleHasEnded && !bookNow && !comingSoon && !isEventNotOnSaleYet && (
        <>
          {
            ((isPresale && isPresaleSoldOut && userIsGda) || (!isPresale && soldOut))
              ? renderSoldOut({
                soldOut, isGda, isPresaleSoldOut, generalSaleDate, publishDate,
              })
              : renderQuantityMoreThanAvailable(
                {
                  quantityMoreThanTickets, availableQuantity, generalAdmission,
                }
              )
          }
        </>
      )}
    </div>
  );
};

EventTicketType.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  availableQuantity: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  basePriceNumber: PropTypes.number.isRequired,
  description: PropTypes.arrayOf(
    PropTypes.string
  ),
  enableBookNow: PropTypes.bool.isRequired,
  event: PropTypes.shape({
    isGda: PropTypes.bool,
    saleHasEnded: PropTypes.bool,
    isPresaleSoldOut: PropTypes.bool,
    generalSaleDate: PropTypes.string,
    title: PropTypes.string,
    timezone: PropTypes.string,
    type: PropTypes.string,
    venueId: PropTypes.number,
  }),
  generalSaleDate: PropTypes.string,
  isEventNotOnSaleYet: PropTypes.bool,
  isGda: PropTypes.bool,
  isPresale: PropTypes.bool,
  isPreSaleOnForGdaUsers: PropTypes.bool,
  isPresaleSoldOut: PropTypes.bool,
  mandatoryAddOns: PropTypes.bool.isRequired,
  price: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  priceNumber: PropTypes.number,
  publishDate: PropTypes.string.isRequired,
  selectedQuantity: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  selectResy: PropTypes.func.isRequired,
  slots: PropTypes.arrayOf(PropTypes.shape({
    badge: PropTypes.string,
    colors: PropTypes.shape({
      background: PropTypes.string,
      font: PropTypes.string,
    }),
    serviceTypeId: PropTypes.number,
    time: PropTypes.string,
    token: PropTypes.string,
    type: PropTypes.string,
  })),
  soldOut: PropTypes.bool,
  taxIncluded: PropTypes.bool,
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf([GENERAL_ADMISSION, SEATED]),
  userIsGda: PropTypes.bool,
};

EventTicketType.defaultProps = {
  description: [],
  event: {},
  generalSaleDate: null,
  isEventNotOnSaleYet: false,
  isGda: false,
  isPresale: false,
  isPreSaleOnForGdaUsers: false,
  isPresaleSoldOut: false,
  price: null,
  priceNumber: 0,
  slots: null,
  soldOut: false,
  taxIncluded: false,
  type: SEATED,
  userIsGda: false,
};

EventTicketType.displayName = 'EventTicketType';
export {
  formatDateAndTime,
  getBookNowOnClick,
  renderBookNowGeneralAdmission,
  renderBookNowGeneralSeated,
  renderComingSoonGeneralAdmission,
  renderGeneralSaleDate,
  renderPresaleGeneralAdmission,
  renderPrice,
  renderSoldOut,
};
export default EventTicketType;
